import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (username: string, password: string) =>
    api.post('/auth/login', { username, password }),

  register: (username: string, email: string, password: string, role: string) =>
    api.post('/auth/register', { username, email, password, role }),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),

  getCurrentUser: () => api.get('/auth/me'),

  getUsers: () => api.get('/auth/users'),

  updateUser: (id: number, data: { email?: string; role?: string; isActive?: boolean }) =>
    api.put(`/auth/users/${id}`, data),

  deleteUser: (id: number) => api.delete(`/auth/users/${id}`),
};

// Vehicle APIs
export const vehicleAPI = {
  // Public
  getVehiclePublic: (plateNumber: string) =>
    api.get(`/vehicles/public/${plateNumber}`),

  // Admin
  getAllVehicles: () => api.get('/vehicles'),

  getVehicle: (id: number) => api.get(`/vehicles/${id}`),

  createVehicle: (data: {
    plateNumber: string;
    vehicleMake?: string;
    vehicleModel?: string;
    year?: number;
    color?: string;
    licenseExpiryDate?: string;
    insuranceExpiryDate?: string;
  }) => api.post('/vehicles', data),

  updateVehicle: (id: number, data: {
    vehicleMake?: string;
    vehicleModel?: string;
    year?: number;
    color?: string;
    licenseExpiryDate?: string;
    insuranceExpiryDate?: string;
    isActive?: boolean;
  }) => api.put(`/vehicles/${id}`, data),

  deleteVehicle: (id: number) => api.delete(`/vehicles/${id}`),

  bulkDeleteVehicles: (ids: number[]) =>
    api.delete('/vehicles/bulk-delete', { data: ids }),

  uploadLicenseImage: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/vehicles/${id}/license-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  uploadInsuranceImage: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/vehicles/${id}/insurance-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  bulkImport: (excelFiles: File[], pdfFiles: File[], zipFiles: File[], limit?: number, serverDir?: string) => {
    const formData = new FormData();
    excelFiles.forEach(f => formData.append('excelFiles', f));
    pdfFiles.forEach(f => formData.append('pdfFiles', f));
    zipFiles.forEach(f => formData.append('zipFiles', f));
    if (limit) formData.append('limit', limit.toString());
    if (serverDir?.trim()) formData.append('serverDir', serverDir.trim());
    return api.post('/vehicles/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600_000,
    });
  },
};

// Company APIs
export const companyAPI = {
  getCompanyInfo: () => api.get('/company'),

  updateCompanyInfo: (data: {
    companyName?: string;
    websiteUrl?: string;
    facebookUrl?: string;
    twitterUrl?: string;
    instagramUrl?: string;
    linkedInUrl?: string;
    phone?: string;
    email?: string;
  }) => api.put('/company', data),

  uploadLogo: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/company/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Audit APIs
export const auditAPI = {
  getAuditLogs: (params?: {
    page?: number;
    pageSize?: number;
    entityType?: string;
    action?: string;
    fromDate?: string;
    toDate?: string;
  }) => api.get('/audit', { params }),

  getAuditLog: (id: number) => api.get(`/audit/${id}`),
};

export default api;
